<template>
  <div id="app">
    <navbar></navbar>
    <transition name="page" mode="out-in">
      <router-view/>
    </transition>
  </div>
</template>

<script>
import navbar from './components/navbar.vue';
export default {
  name: 'HomeView',
  components: {
    navbar
  }
}
</script>


<style lang="scss">
@import './styles/variables.scss';
@import './styles/mixins.scss';

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: $font-family;
  color: $text-primary;
  background: $bg-secondary;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.page-enter {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

a {
  color: $primary-color;
  text-decoration: none;
  transition: color $transition-fast;
  
  &:hover {
    color: $primary-dark;
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: $border-radius;
  border: none;
  cursor: pointer;
  transition: all $transition;
  gap: 8px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.3);
  }
  
  &-primary {
    background: $bg-gradient;
    color: white;
    box-shadow: $shadow;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: $shadow-primary;
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &-outline {
    background: transparent;
    color: $primary-color;
    border: 2px solid $primary-color;
    
    &:hover {
      background: $primary-color;
      color: white;
    }
  }
  
  &-light {
    background: white;
    color: $primary-color;
    box-shadow: $shadow;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: $shadow-md;
    }
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @include responsive(md) {
    padding: 0 15px;
  }
}

.slice {
  padding: 80px 0;
  
  @include responsive(md) {
    padding: 40px 0;
  }
}

.lang-header {
  text-align: left;
  margin-top: 100px;
  margin-bottom: 40px;

  .step-box {
    padding: 24px 16px;
    height: 100%;
    border-radius: $border-radius-lg;
    background: white;
    box-shadow: $shadow;
    transition: all $transition;
    border: 1px solid transparent;

    &:hover {
      transform: translateY(-4px);
      box-shadow: $shadow-lg;
      border-color: rgba($primary-color, 0.2);
    }

    .step-title {
      font-size: 20px;
      color: $text-primary;
      font-weight: 700;
      text-align: center;
      margin-bottom: 16px;
    }

    .step-age {
      font-size: 14px;
      margin: 16px 0;
      text-align: center;

      .age {
        background: $bg-gradient;
        border-radius: 20px;
        width: 80px;
        margin: auto;
        padding: 6px 12px;
        text-align: center;
        color: white;
        font-weight: 600;
        font-size: 13px;
      }
    }

    .step-desc {
      color: $text-secondary;
      font-size: 14px;
      line-height: 1.7;
      text-align: center;
    }
  }
}

.card {
  background: white;
  border-radius: $border-radius-lg;
  box-shadow: $shadow;
  overflow: hidden;
  transition: all $transition;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: $shadow-lg;
  }
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  color: $text-primary;
  text-align: center;
  margin-bottom: 16px;
  
  @include responsive(md) {
    font-size: 24px;
  }
}

.section-subtitle {
  font-size: 16px;
  color: $text-secondary;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 48px;
  
  @include responsive(md) {
    font-size: 14px;
    margin-bottom: 32px;
  }
}

.gradient-text {
  @include gradient-text;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  
  &-primary {
    background: rgba($primary-color, 0.1);
    color: $primary-color;
  }
  
  &-success {
    background: rgba($success-color, 0.1);
    color: $success-color;
  }
}

.list-group {
  border-radius: $border-radius-lg;
  overflow: hidden;
  box-shadow: $shadow;
  
  .list-group-item {
    padding: 16px 20px;
    border: none;
    border-bottom: 1px solid $border-color;
    background: white;
    transition: all $transition-fast;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background: rgba($primary-color, 0.05);
      padding-left: 24px;
    }
  }
}

hr {
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, $border-color, transparent);
  margin: 48px 0;
}

img {
  max-width: 100%;
  height: auto;
}

input, select, textarea {
  font-family: inherit;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
}

.form-control {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid $border-color;
  border-radius: $border-radius;
  background: white;
  transition: all $transition-fast;
  
  &:focus {
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
  
  &::placeholder {
    color: $text-muted;
  }
}

::-webkit-selection {
  background: rgba($primary-color, 0.2);
}

::selection {
  background: rgba($primary-color, 0.2);
}

@include scroll-style;
</style>
